import { useSearchParams } from '@remix-run/react'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { CheckCircle, WarningHexagon } from 'iconoir-react'
import { camelCase, startCase } from 'lodash'
import moment from 'moment'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { z } from 'zod'
import FormCombo from '../form/FormCombo'
import FormSelect from '../form/FormSelect'
import FormTextArea from '../form/FormTextArea'
import { SchedulingWizardContext } from './SchedulingWizard'
import FormInput from 'app/components/form/FormInput'
import FormPhone from 'app/components/form/FormPhone'
import { SmartForm } from 'app/components/form/SmartForm'
import { LocationJson } from 'app/models/Location'
import { CustomQuestionType, SchedulingConfigurationJson } from 'app/models/SchedulingConfiguration'
import { bg } from 'lib/background'
import { INSURANCE_LIST } from 'types/InsuranceList'
import { OptionType } from 'types/OptionType'

export const appointmentRequestSchema = (customQuestions?: CustomQuestionType[]) => {
  const extraFields =
    customQuestions?.reduce((acc, q) => {
      if (!q.enabled) return acc
      return {
        ...acc,
        [camelCase(q.fieldName)]: z
          .string()
          .refine((val) => (q.required ? !!val : true), { message: `${startCase(q.fieldName)} is required` }),
      }
    }, {}) ?? {}

  return withZod(
    z
      .object({
        firstName: z.string().refine((val) => val !== ``, { message: `First name is required` }),
        lastName: z.string().refine((val) => val !== ``, { message: `Last name is required` }),
        email: z.string().email(),
        phone: z.string().refine(
          (val) => {
            const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/
            return phoneRegex.test(val) || /^\d{10}$/.test(val)
          },
          {
            message: `Phone number is not valid`,
          },
        ),
        dob: z
          .string()
          .refine(
            (val) =>
              val !== `` &&
              val.length === 10 &&
              (moment(val, `MM/DD/YYYY`).isValid() || moment(val, `YYYY-MM-DD`).isValid()),
            {
              message: `Date of birth is not valid`,
            },
          )
          .transform((val) => {
            // Kolla appt required date format 'YYYY-MM-DD'
            return moment(val, `MM/DD/YYYY`).format(`YYYY-MM-DD`)
          }),
        insurance: z.string().min(1, { message: `Please select an insurance provider` }),
        // otherInsurance is required only if insurance is other
        otherInsurance: z.string().optional(),
        preferredDate: z
          .string()
          .refine(
            (val) =>
              val !== `` &&
              val.length === 10 &&
              (moment(val, `MM/DD/YYYY`).isValid() || moment(val, `YYYY-MM-DD`).isValid()),
            {
              message: `Preferred date is not valid`,
            },
          )
          .transform((val) => {
            // Kolla appt required date format 'YYYY-MM-DD'
            return moment(val, `MM/DD/YYYY`).format(`YYYY-MM-DD`)
          }),
        preferredTime: z.string().refine((val) => val !== ``, { message: `Preferred time is required` }),
        submissionDate: z.string(),
        appointmentType: z.string(),
        utm_source: z.string().optional(),
        utm_medium: z.string().optional(),
        utm_campaign: z.string().optional(),
        utm_content: z.string().optional(),
        gclid: z.string().optional(),
        fbclid: z.string().optional(),
        ...extraFields,
      })
      .superRefine(({ preferredDate, insurance, otherInsurance }, ctx) => {
        if (insurance === `Other` && !otherInsurance) {
          ctx.addIssue({
            code: `custom`,
            message: `Please provide the name of your insurance provider`,
            path: [`otherInsurance`],
          })
        }
        if (moment(preferredDate, `YYYY-MM-DD`).isBefore(moment().format(`YYYY-MM-DD`))) {
          ctx.addIssue({ code: `custom`, message: `Date must be in the future`, path: [`preferredDate`] })
        }
      }),
  )
}

export function OverrideForm({
  location,
  schedulingConfiguration,
}: {
  location?: LocationJson
  schedulingConfiguration?: SchedulingConfigurationJson
}): ReactElement {
  const {
    apptType,
    back,
    patient,
    setIsFormComplete,
    setPatient,
    setIsSaving,
    setCurrentStep,
    setFlowMode,
    setSelectedInsurance,
  } = useContext(SchedulingWizardContext)
  const [searchParams] = useSearchParams()
  const [selectedInsuranceOption, setSelectedInsuranceOption] = useState<OptionType>()

  const onCreateAppointmentComplete = (data) => {
    if (data.form) {
      setIsSaving(false)
      setCurrentStep(4)
    }
  }

  const validateForm = () => {
    const form = document.getElementById(`appointment-form`) as HTMLFormElement
    if (form) {
      const formData = new FormData(form)
      const data = Object.fromEntries(formData.entries())
      const textareas = form.querySelectorAll(`textarea`)
      const textAreaInputs = Array.from(textareas).reduce((acc, textarea) => {
        acc[textarea.name] = textarea.value
        return acc
      }, {})

      bg(async () => {
        const result = await appointmentRequestSchema(schedulingConfiguration?.customQuestions).validate({
          ...data,
          ...textAreaInputs,
          insurance: selectedInsuranceOption?.value, // Include the selected insurance value
        })
        if (result.error) {
          setIsFormComplete(false)
        } else {
          setPatient({
            firstName: data[`firstName`] as string,
            lastName: data[`lastName`] as string,
            email: data[`email`] as string,
            phone: data[`phone`] as string,
            dob: data[`dob`] as string,
          })
          setIsFormComplete(true)
        }
      })
    }
  }

  useEffect(() => {
    setSelectedInsurance(selectedInsuranceOption?.value)
    // Trigger form validation when insurance selection changes
    validateForm()
  }, [selectedInsuranceOption, schedulingConfiguration?.customQuestions])

  useEffect(() => {
    const form = document.getElementById(`appointment-form`) as HTMLFormElement
    const textareas = form.querySelectorAll(`textarea`)

    form.addEventListener(`input`, validateForm)
    textareas.forEach((textarea) => {
      textarea.addEventListener(`input`, validateForm)
    })
    return () => {
      form.removeEventListener(`input`, validateForm)

      textareas.forEach((textarea) => {
        textarea.removeEventListener(`input`, validateForm)
      })
    }
  }, [document.getElementById(`appointment-form`), schedulingConfiguration?.customQuestions])

  useEffect(() => {
    setFlowMode(`override`)
  }, [])

  return (
    <div
      className={`flex grow flex-col gap-y-6 overflow-hidden animate-duration-[400ms] ${
        back ? `animate-fade-right` : `animate-fade-left`
      }`}
    >
      <div className="flex grow flex-col gap-2 overflow-y-auto">
        <SmartForm
          id="appointment-form"
          className="!mt-0"
          validator={appointmentRequestSchema(schedulingConfiguration?.customQuestions)}
          defaultValues={{ ...patient }}
          hiddenInputs={[
            { name: `id`, value: location?.id },
            { name: `submissionDate`, value: moment().toISOString() },
            { name: `appointmentType`, value: apptType?.alias ?? apptType?.displayName },
            { name: `insurance`, value: selectedInsuranceOption?.value },
            ...Array.from(searchParams)
              .filter(
                ([key, value]) =>
                  value && [`utm_source`, `utm_medium`, `utm_campaign`, `utm_content`, `gclid`, `fbclid`].includes(key),
              )
              .map(([key, value]) => ({ name: key, value })),
          ]}
          shouldPreventDefault
          reloadDocument={false}
          onSubmit={() => setIsSaving(true)}
          onPreventDefault={onCreateAppointmentComplete}
          hideButtons
          method="POST"
          action={`/api/scheduling/locations/${location?.id}/appointment-request`}
        >
          <FormInput
            name="firstName"
            labelClassName="text-neutral-700 text-sm required"
            label="First Name"
            cols={3}
            required
          />
          <FormInput
            name="lastName"
            labelClassName="text-neutral-700 text-sm required"
            label="Last Name"
            cols={3}
            required
          />

          <FormInput
            cols={3}
            name="dob"
            labelClassName="text-neutral-700 text-sm required"
            label="Birth Date"
            placeholder="mm/dd/yyyy"
            className="placeholder:text-neutral-400"
            type="text"
            onChange={(evt) => {
              let value = evt.currentTarget.value

              // Remove any non-numeric characters except slashes
              value = value.replace(/[^0-9/]/g, ``)

              // Remove slashes to get just the numbers
              const numbersOnly = value.replace(/\//g, ``)

              // Rebuild the value with proper formatting
              let formattedValue = ``

              if (numbersOnly.length >= 2) {
                formattedValue = numbersOnly.substring(0, 2)
                if (numbersOnly.length > 2) {
                  formattedValue += `/` + numbersOnly.substring(2, 4)
                  if (numbersOnly.length > 4) {
                    formattedValue += `/` + numbersOnly.substring(4, 8)
                  }
                }
              } else {
                formattedValue = numbersOnly
              }

              evt.currentTarget.value = formattedValue

              // Validate if we have a complete date
              if (formattedValue.length === 10) {
                const date = moment(formattedValue, `MM/DD/YYYY`)
                if (!date.isValid()) {
                  evt.currentTarget.value = ``
                }
              }
            }}
            required
          />
          <FormPhone
            name="phone"
            labelClassName="text-neutral-700 text-sm required"
            label="Phone Number"
            cols={3}
            maxLength={10}
            minLength={10}
            required
          />
          <FormInput name="email" labelClassName="text-neutral-700 text-sm required" label="Email" cols={6} required />
          <FormCombo
            name="_insurance"
            label="Insurance"
            labelClassName="text-neutral-700 text-sm required"
            optionsContainerClassName="!max-h-[220px]"
            options={[
              { name: `I'll pay for myself`, value: `Self Paid`, id: `Self Paid` },
              { name: `Other`, value: `Other`, id: `Other` },
              ...INSURANCE_LIST.map((insurance) => ({
                name: insurance,
                value: insurance,
                id: insurance,
              })),
            ]}
            showSearchInDropdown
            value={selectedInsuranceOption}
            onChange={(option) => setSelectedInsuranceOption(option)}
            placeholder="Select your insurance provider"
            required
            cols={6}
          />
          {schedulingConfiguration?.notifyInOutNetwork &&
            selectedInsuranceOption &&
            selectedInsuranceOption?.value !== `Self Paid` &&
            selectedInsuranceOption?.value !== `Other` && (
              <div
                className={classNames(
                  `col-span-12 -mt-3 flex items-center gap-2.5 text-body-xs md:col-span-6`,
                  schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) &&
                    `text-success-default`,
                  !schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) &&
                    !schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) &&
                    `text-amber-800`,
                  schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) &&
                    `text-red-600`,
                )}
              >
                {schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) && (
                  <>
                    <CheckCircle width={20} height={20} />
                    <p>Your selected insurance is in network.</p>
                  </>
                )}
                {!schedulingConfiguration?.insurancesAccepted?.includes(selectedInsuranceOption?.value) &&
                  !schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) && (
                    <>
                      <WarningHexagon width={20} height={20} />
                      <p>Your selected insurance is considered out of network.</p>
                    </>
                  )}
                {schedulingConfiguration?.insurancesRejected?.includes(selectedInsuranceOption?.value) && (
                  <>
                    <WarningHexagon width={20} height={20} />
                    <p>Your selected insurance is not accepted.</p>
                  </>
                )}
              </div>
            )}
          {selectedInsuranceOption?.value === `Other` && (
            <FormInput
              name="otherInsurance"
              label="Insurance Provider"
              labelClassName="text-neutral-700 text-sm required"
              placeholder="Insurance Provider Name (Other)"
              cols={6}
            />
          )}
          <FormInput
            cols={3}
            name="preferredDate"
            label="Preferred Date"
            labelClassName="text-neutral-700 text-sm required"
            placeholder="mm/dd/yyyy"
            className="placeholder:text-neutral-400"
            type="text"
            onChange={(evt) => {
              const value = evt.currentTarget.value.replace(/[^0-9]/g, ``)
              evt.currentTarget.value = value
              if (value.length === 8) {
                const date = moment(value, `MMDDYYYY`)
                evt.currentTarget.value = date.isValid() ? date.format(`MM/DD/YYYY`) : ``
              }
            }}
            required
          />
          <FormSelect
            name="preferredTime"
            label="Preferred Time"
            labelClassName="text-neutral-700 text-sm required"
            required
            cols={3}
            options={[
              { value: `morning`, label: `Morning` },
              { value: `afternoon`, label: `Afternoon` },
            ]}
          />
          {schedulingConfiguration?.customQuestions?.map((question) => {
            if (!question.enabled) return null
            if (question.fieldType === `one-line`) {
              return (
                <FormInput
                  key={question.id}
                  name={camelCase(question.fieldName)}
                  label={startCase(question.fieldName)}
                  labelClassName={`text-neutral-700 text-sm ${question.required ? `required` : ``}`}
                  required={question.required}
                  cols={6}
                />
              )
            } else if (question.fieldType === `multiple-lines`) {
              return (
                <FormTextArea
                  key={question.id}
                  name={camelCase(question.fieldName)}
                  label={startCase(question.fieldName)}
                  labelClassName={`text-neutral-700 text-sm ${question.required ? `required` : ``}`}
                  className="h-24 resize-none overflow-y-auto px-4 py-3.5 placeholder:text-neutral-400"
                  placeholder="Anything we should know?"
                  required={question.required}
                  cols={6}
                  maxLength={200}
                />
              )
            }
            return null
          })}
        </SmartForm>
      </div>
    </div>
  )
}
